import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, MessageCircle, Send, Navigation } from "lucide-react";
import { COMPANY, BRANDS, CATEGORIES } from "../data/site";
import { Reveal } from "../components/Reveal";
import { useTheme } from "../components/ThemeProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, children, required }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-overline font-bold text-muted-foreground mb-2">
      {label} {required && <span className="text-[#C8102E]">*</span>}
    </span>
    {children}
  </label>
);

const inputClass =
  "w-full h-11 px-3 bg-background border border-border focus:border-[#C8102E] focus:outline-none focus:ring-1 focus:ring-[#C8102E] text-sm text-foreground placeholder:text-muted-foreground transition-colors duration-150";

const Contact = () => {
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    vehicle_brand: "",
    vehicle_model: "",
    required_part: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const part = searchParams.get("part");
    const brand = searchParams.get("brand");
    if (part || brand) {
      setForm((f) => ({
        ...f,
        required_part: part || f.required_part,
        vehicle_brand: brand || f.vehicle_brand,
      }));
    }
  }, [searchParams]);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await axios.post(`${API}/enquiries`, payload);
      toast.success("Enquiry sent — our team will contact you shortly.");
      setForm({
        name: "",
        phone: "",
        email: "",
        vehicle_brand: "",
        vehicle_model: "",
        required_part: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact" className="bg-background">
      {/* HEADER */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-20">
          <Reveal>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-12 bg-[#C8102E]" />
              <span className="text-xs uppercase tracking-overline font-bold text-muted-foreground">
                Contact
              </span>
            </div>
            <h1 className="font-display font-black uppercase text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-[0.95] max-w-3xl">
              Let&apos;s find the <br />
              <span className="text-[#C8102E]">right part.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Send us your vehicle details and requirement — our team will get
              back to you with availability and best pricing.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Contact info */}
          <div className="lg:col-span-4 space-y-8">
            <Reveal>
              <div className="border-l-2 border-[#C8102E] pl-5 space-y-1">
                <div className="text-[11px] uppercase tracking-overline text-muted-foreground">
                  Owner
                </div>
                <div className="font-display font-bold text-xl">
                  {COMPANY.owner}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <ul className="space-y-5">
                <li className="flex gap-4">
                  <span className="h-10 w-10 shrink-0 border border-border flex items-center justify-center text-[#C8102E]">
                    <MapPin className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-overline text-muted-foreground">
                      Address
                    </div>
                    <a
                      href={COMPANY.mapView}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-address-link"
                      className="mt-1 block text-sm leading-relaxed hover:text-[#C8102E] transition-colors duration-200"
                    >
                      {COMPANY.address.line1},<br />
                      {COMPANY.address.city}, {COMPANY.address.state} –{" "}
                      {COMPANY.address.pin}
                    </a>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="h-10 w-10 shrink-0 border border-border flex items-center justify-center text-[#C8102E]">
                    <Phone className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-overline text-muted-foreground">
                      Phone
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {COMPANY.phones.map((p) => (
                        <a
                          key={p.link}
                          href={`tel:${p.link}`}
                          className="block text-sm hover:text-[#C8102E]"
                          data-testid={`contact-phone-${p.link}`}
                        >
                          {p.display}
                        </a>
                      ))}
                    </div>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="h-10 w-10 shrink-0 border border-border flex items-center justify-center text-[#C8102E]">
                    <Mail className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-overline text-muted-foreground">
                      Email
                    </div>
                    <a
                      href={`mailto:${COMPANY.email}`}
                      className="mt-1 block text-sm hover:text-[#C8102E] break-all"
                    >
                      {COMPANY.email}
                    </a>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="h-10 w-10 shrink-0 border border-border flex items-center justify-center text-[#C8102E]">
                    <Clock className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-overline text-muted-foreground">
                      Business Hours
                    </div>
                    <div className="mt-1 text-sm">{COMPANY.hours}</div>
                  </div>
                </li>
              </ul>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex flex-col gap-3">
                {COMPANY.whatsapps.map((w, idx) => (
                  <a
                    key={w.link}
                    href={`https://wa.me/${w.link}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`contact-whatsapp-${idx}`}
                    className="inline-flex items-center gap-2 h-12 px-5 bg-[#25D366] text-white text-sm font-bold uppercase tracking-overline hover:opacity-90 transition-opacity duration-200"
                  >
                    <MessageCircle className="h-4 w-4" />
                    WhatsApp {w.display}
                  </a>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-8">
            <Reveal>
              <form
                onSubmit={onSubmit}
                data-testid="enquiry-form"
                className="border border-border bg-card p-6 lg:p-8 space-y-5"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Name" required>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={onChange}
                      required
                      className={inputClass}
                      data-testid="form-name"
                      placeholder="Your full name"
                    />
                  </Field>
                  <Field label="Phone Number" required>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      required
                      className={inputClass}
                      data-testid="form-phone"
                      placeholder="+91-XXXXXXXXXX"
                    />
                  </Field>
                  <Field label="Email">
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={onChange}
                      className={inputClass}
                      data-testid="form-email"
                      placeholder="you@example.com"
                    />
                  </Field>
                  <Field label="Vehicle Brand">
                    <select
                      name="vehicle_brand"
                      value={form.vehicle_brand}
                      onChange={onChange}
                      className={inputClass}
                      data-testid="form-brand"
                    >
                      <option value="">Select brand</option>
                      {BRANDS.map((b) => (
                        <option key={b.key} value={b.name}>
                          {b.name}
                        </option>
                      ))}
                      <option value="Other">Other</option>
                    </select>
                  </Field>
                  <Field label="Vehicle Model">
                    <input
                      type="text"
                      name="vehicle_model"
                      value={form.vehicle_model}
                      onChange={onChange}
                      className={inputClass}
                      data-testid="form-model"
                      placeholder="e.g. Bolero / Swift / Nexon"
                    />
                  </Field>
                  <Field label="Required Spare Part">
                    <input
                      type="text"
                      name="required_part"
                      value={form.required_part}
                      onChange={onChange}
                      className={inputClass}
                      data-testid="form-part"
                      placeholder="e.g. Brake pads, Clutch kit"
                      list="parts-list"
                    />
                    <datalist id="parts-list">
                      {CATEGORIES.map((c) => (
                        <option key={c.key} value={c.name} />
                      ))}
                    </datalist>
                  </Field>
                </div>
                <Field label="Message">
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={5}
                    className={`${inputClass} h-auto py-3 resize-y`}
                    data-testid="form-message"
                    placeholder="Share any details that will help us serve you better..."
                  />
                </Field>
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="form-submit"
                    className="inline-flex items-center gap-2 h-12 px-6 bg-[#C8102E] text-white text-sm font-bold uppercase tracking-overline hover:bg-[#A00D24] disabled:opacity-60 transition-colors duration-200"
                  >
                    <Send className="h-4 w-4" />
                    {submitting ? "Sending..." : "Send Enquiry"}
                  </button>
                  <p className="text-xs text-muted-foreground">
                    We typically respond within a few business hours.
                  </p>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-5">
              <div>
                <div className="text-[11px] uppercase tracking-overline text-muted-foreground">
                  Find us on the map
                </div>
                <h3 className="mt-1 font-display font-bold text-xl md:text-2xl">
                  Visit our store in Berhampore
                </h3>
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href={COMPANY.mapDirections}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="map-directions"
                  className="inline-flex items-center gap-2 h-11 px-5 bg-[#C8102E] text-white text-xs font-bold uppercase tracking-overline hover:bg-[#A00D24] transition-colors duration-200"
                >
                  <Navigation className="h-4 w-4" /> Get Directions
                </a>
                <a
                  href={COMPANY.mapView}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="map-view"
                  className="inline-flex items-center gap-2 h-11 px-5 border border-border hover:border-[#C8102E] text-xs font-bold uppercase tracking-overline transition-colors duration-200"
                >
                  <MapPin className="h-4 w-4" /> Open in Google Maps
                </a>
              </div>
            </div>
            <a
              href={COMPANY.mapView}
              target="_blank"
              rel="noopener noreferrer"
              className="block border border-border overflow-hidden group"
              aria-label="Open RNS Automobiles location in Google Maps"
            >
              <iframe
                title="RNS Automobiles Location"
                src={COMPANY.mapEmbed}
                width="100%"
                height="440"
                style={{ border: 0, pointerEvents: "none" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className={theme === "dark" ? "map-dark" : ""}
                data-testid="google-map"
              />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
